/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React from 'react';

import { useSelector } from 'react-redux';
import { Ducks } from '@wit/vtv-x-foundation-lib';
import { Redirect, RouteProps } from 'react-router-dom';
import { EProfilePageStatus } from 'ui/pages/profiles/profile.interface';
import {
  Folder,
  ManageAccessibilityPage,
  OnDemand,
  ParentalControlPage,
  Profiles,
  Settings,
  WatchTv,
  ManagePinPage,
  MyTV,
  GenreRailsLevel,
  GridLevel,
  WatchNextThirdLevel,
  ProgrammeInfo,
} from 'ui/pages';
import AnimatedRoute from './AnimatedRoute';
import { PrivateRoutePath, StartUpRoutePath } from './paths';

type PrivateRouteProps = RouteProps &
  React.Attributes & {
    path: PrivateRoutePath;
    children: JSX.Element;
  };

function PrivateRoute({ path, children, key, exact = false }: PrivateRouteProps): JSX.Element {
  const isUserAuthenticated = useSelector(Ducks.Credentials.Selectors.isAuthenticated);
  const isProcessing = useSelector(Ducks.Credentials.Selectors.isCredentialsProcessing);

  return (
    <AnimatedRoute
      path={path}
      key={key}
      exact={exact}
      render={({ location }) => {
        return isUserAuthenticated || isProcessing ? (
          children
        ) : (
          <Redirect
            to={{ pathname: StartUpRoutePath.ROOT, state: { from: `${location.pathname}${location.search}` } }}
          />
        );
      }}
    />
  );
}

function ProfilesRoutes(): JSX.Element[] {
  return [
    <PrivateRoute path={PrivateRoutePath.PROFILES_LIST} key={PrivateRoutePath.PROFILES_LIST}>
      <Profiles mode={EProfilePageStatus.LIST} />
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.PROFILES_ADD} key={PrivateRoutePath.PROFILES_ADD}>
      <Profiles mode={EProfilePageStatus.CREATE} />
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.PROFILES_EDIT} key={PrivateRoutePath.PROFILES_EDIT}>
      <Profiles mode={EProfilePageStatus.EDIT} />
    </PrivateRoute>,
  ];
}

function SettingsRoutes(): JSX.Element[] {
  return [
    <PrivateRoute path={PrivateRoutePath.SETTINGS} key={PrivateRoutePath.SETTINGS} exact>
      <Settings />
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.SETTINGS_PARENTAL} key={PrivateRoutePath.SETTINGS_PARENTAL}>
      <ParentalControlPage />
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.SETTINGS_PIN} key={PrivateRoutePath.SETTINGS_PIN}>
      <ManagePinPage />
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.SETTINGS_ACCESSIBILITY} key={PrivateRoutePath.SETTINGS_ACCESSIBILITY}>
      <ManageAccessibilityPage />
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.SETTINGS_PROFILES_ADD} key={PrivateRoutePath.SETTINGS_PROFILES_ADD}>
      <Profiles mode={EProfilePageStatus.CREATE} />
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.SETTINGS_PROFILES_EDIT} key={PrivateRoutePath.SETTINGS_PROFILES_EDIT}>
      <Profiles mode={EProfilePageStatus.EDIT} />
    </PrivateRoute>,
  ];
}

function MyTVRoutes(): JSX.Element[] {
  return [
    <PrivateRoute path={PrivateRoutePath.MYTV} key={PrivateRoutePath.MYTV} exact>
      <MyTV />
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.MYTV_WATCH_NEXT} key={PrivateRoutePath.MYTV_WATCH_NEXT}>
      <WatchNextThirdLevel />
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.MYTV_RAIL} key={PrivateRoutePath.MYTV_RAIL} exact>
      <GenreRailsLevel />
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.MYTV_RAIL_CATEGORY} key={PrivateRoutePath.MYTV_RAIL_CATEGORY} exact>
      <GridLevel />
    </PrivateRoute>,
  ];
}

function WatchTvRoutes(): JSX.Element[] {
  return [
    <PrivateRoute path={PrivateRoutePath.WATCHTV} key={PrivateRoutePath.WATCHTV} exact>
      <WatchTv />
    </PrivateRoute>,
  ];
}

function OnDemandRoutes(): JSX.Element[] {
  return [
    <PrivateRoute path={PrivateRoutePath.ONDEMAND} key={PrivateRoutePath.ONDEMAND} exact>
      <OnDemand />
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.ONDEMAND_NEXT_LEVEL} key={PrivateRoutePath.ONDEMAND_NEXT_LEVEL} exact>
      <OnDemand onNextLevel />
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.ONDEMAND_GRID} key={PrivateRoutePath.ONDEMAND_GRID} exact>
      <OnDemand onGridMode />
    </PrivateRoute>,
  ];
}

function ProgramInfoRoutes(): JSX.Element[] {
  return [
    <PrivateRoute path={PrivateRoutePath.PROGRAM_INFO} key={PrivateRoutePath.PROGRAM_INFO}>
      <ProgrammeInfo />
    </PrivateRoute>,
  ];
}

function FolderRoutes(): JSX.Element[] {
  return [
    <PrivateRoute path={PrivateRoutePath.FOLDER_INFO_GRID} key={PrivateRoutePath.FOLDER_INFO_GRID}>
      <Folder onGridMode />
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.FOLDER_INFO} key={PrivateRoutePath.FOLDER_INFO} exact>
      <Folder />
    </PrivateRoute>,
  ];
}

export default function PrivateRoutes(): JSX.Element[] {
  return [
    ...ProfilesRoutes(),
    ...SettingsRoutes(),
    ...MyTVRoutes(),
    ...WatchTvRoutes(),
    ...OnDemandRoutes(),
    ...FolderRoutes(),
    ...ProgramInfoRoutes(),

    <PrivateRoute path={PrivateRoutePath.HELP} key={PrivateRoutePath.HELP}>
      {/* The page component goes here */}
      <p>ComponentGoesHere</p>
    </PrivateRoute>,
    <PrivateRoute path={PrivateRoutePath.SEARCH} key={PrivateRoutePath.SEARCH}>
      {/* The page component goes here */}
      <p>ComponentGoesHere</p>
    </PrivateRoute>,
  ];
}
